// ── Release a referral source ("remove me as owner") ──
//
// The one ownership change a rep can make: giving up their own source. The
// owner is cleared and the record waits, unassigned, for an administrator to
// reassign. Nothing is deleted. The reason is required and lands on the
// source's Activity timeline for whoever owns it next. See
// api/releaseReferralSource for the server-side rules.
import { useState } from "react";
import { B } from "../../../shared/theme";
import { Btn, Field, Modal } from "../../../shared/ui";
import { useApp } from "../../../shared/appContext";

// Whether to OFFER the button. The server re-checks: owner only.
export const canRelease = (rs, currentUser, role) =>
  !!(rs && rs.owner && currentUser && role && role.canEdit && String(rs.owner) === String(currentUser.id));

// onEditInstead: the referral source changed firms. That is an edit, not a
// release — see the callout below.
export const ReleaseReferralSourceModal = ({ rs, onClose, onDone, onEditInstead }) => {
  const { api } = useApp();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const name = rs.company_name || rs.full_name || "this referral source";
  const ok = reason.trim().length >= 3;

  const go = async () => {
    setBusy(true); setErr(null);
    try {
      await api.post("releaseReferralSource", { id: rs.id, reason: reason.trim() });
      onDone && onDone();
    } catch (e) { setErr(e.message || "Could not release"); setBusy(false); }
  };

  return (
    <Modal title="Remove me as owner" onClose={busy ? null : onClose}>
      <div style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 12 }}>
        You're giving up <strong>{name}</strong>. Nothing is deleted — its leads, opportunities
        and history stay. It becomes <strong>unassigned</strong> until an administrator gives it to someone,
        and you won't see it in your list after this.
      </div>
      {/* The most common wrong reason to release: the person moved firms. The
          relationship is the person, so it stays on this record — edit the
          company and email. Releasing and re-creating would split the history
          across two records and hand the old one to whoever an admin picks. */}
      <div style={{ fontSize: 12.5, lineHeight: 1.55, background: B.cyanLight, border: "1px solid " + B.cyanDark,
                    borderRadius: 6, padding: "10px 12px", marginBottom: 12 }}>
        <div style={{ fontWeight: 700, color: B.cyanDark, marginBottom: 4 }}>Did they just change firms?</div>
        Don't remove yourself and create a new one. <strong>Edit this record</strong> instead: update the company
        name and the email address. You keep the relationship, and all the email history stays on it. Mail to the
        new address attaches from now on, including anything already captured from it.
        {onEditInstead && (
          <div style={{ marginTop: 8 }}>
            <Btn variant="primary" onClick={onEditInstead} disabled={busy}>Edit this record instead</Btn>
          </div>
        )}
      </div>
      <div style={{ fontSize: 12, color: B.amberDark, background: B.amberLight, border: "1px solid " + B.amberMid,
                    borderRadius: 6, padding: "8px 10px", marginBottom: 12, lineHeight: 1.5 }}>
        Your open follow-ups on it are dismissed. Getting it back means asking an administrator.
      </div>
      <Field label="Why are you letting it go?" required>
        <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} autoFocus
          placeholder="e.g. Left the firm, no longer refers our deal size, relationship belongs to Ryan"
          style={{ width: "100%", boxSizing: "border-box", padding: "7px 10px", fontSize: 13, borderRadius: 4,
                   border: "1px solid #999", fontFamily: "inherit", resize: "vertical" }} />
      </Field>
      <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 4 }}>
        Saved to the source's Activity timeline, for the next owner.
      </div>
      {err && <div style={{ color: "#993C1D", fontSize: 12, marginTop: 10 }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
        <Btn onClick={onClose} disabled={busy}>Cancel</Btn>
        <Btn variant="danger" onClick={go} disabled={busy || !ok}>{busy ? "Releasing…" : "Remove me as owner"}</Btn>
      </div>
    </Modal>
  );
};
